import { inject, Injectable } from '@angular/core';
import maplibregl, {
  addProtocol,
  AddLayerObject,
  CanvasSourceSpecification,
  LayerSpecification,
  Map as MapLibreMap,
  MapLayerMouseEvent,
  Marker, // alias per la mappa di maplibre-gl
  Popup,
  Source,
  SourceSpecification,
} from 'maplibre-gl';
import { ISJMapSettings } from '../interfaces/explorer/interfaces/ISJMapSettings';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { createMapLibreDrawStyles } from '../constants/maplibre-draw.styles';
import { Store } from '@ngrx/store';
import { HttpClient } from '@angular/common/http';
import { ISJMap } from '../interfaces/explorer/interfaces/ISJMap';
import { BehaviorSubject, filter, Observable, Subject, take } from 'rxjs';
import { Feature, Geometry, Point } from 'geojson';
import { bbox, area } from '@turf/turf';
import {
  selectExplorerSJMap,
  selectExplorerSJMapRasterActiveSourceByUUID,
  selectExplorerSJMapVectorActiveSourceByUUID,
} from '../store/selectors/explorer/explorer-sjmap.selector';
import { ExplorerSJMapState } from '../store/reducers/explorer/explorer-sjmap.reducer';
import { TLayer } from '../types/explorer/sjmap2/TLayer';
import { addLayer } from '../store/actions/explorer/explorer-sjmap.actions';
import {
  addAnalyticsData,
  cleanAnalyticsData,
} from '../store/actions/explorer/explorer-analytics-tool.actions';
import { PMTiles, Protocol } from 'pmtiles';
import { ExplorerAddSJMapLayerToMapService } from './explorer/explorer-add-sjmap-layer-to-map/explorer-add-sjmap-layer-to-map.service';
import { ExplorerReorderSJMapLayersOnMapService } from './explorer/explorer-reorder-sjmap-layers-on-map/explorer-reorder-sjmap-layers-on-map.service';
import { TitilerNewService } from './shared/titiler-new.service';
import { IZoom } from '../interfaces/explorer/interfaces/IZoom';
import { AnalyticsDrawStateService } from './explorer/analytics-draw-state/analytics-draw-state.service';
import { IThread } from '../interfaces/living-lab/IThread';
import { LivingLabService } from './living-lab.service';
import { KpiSidebarService } from './kpi-sidebar.service';
import { ExplorerSplitMapService } from './explorer/explorer-splitmap/explorer-splitmap.service';
import { AnalyticsToolService } from './explorer/analytics-tool/analytics-tool.service';
import { SimulationDrawClassesService } from './simulation/simulation-draw-classes.service';
import { RasterTargetResolverService } from './shared/raster-target-resolver.service';
import { TColormap } from '../types/explorer/sjmap2/colormap/TColormap';
import { KpiRasterService } from './shared/raster-kpi/kpi-raster.service';
import { IVectorSource } from '../interfaces/sjmap2/vector/IVectorSource';
import { MartinService } from './shared/martin.service';

interface RasterDrawingTableColumn {
  key: string; // Chiave dei dati
  label: string; // Nome della colonna nella tabella
}

interface RasterDrawingTableData {
  [key: string]: number | string; // Valori per ogni riga
}

@Injectable({
  providedIn: 'root',
})
export class ExplorerMapService {
  private store: Store = inject(Store);
  private http: HttpClient = inject(HttpClient);
  private explorerAddSJMapLayerToMapService: ExplorerAddSJMapLayerToMapService =
    inject(ExplorerAddSJMapLayerToMapService);
  private explorerReorderSJMapLayersOnMapService: ExplorerReorderSJMapLayersOnMapService =
    inject(ExplorerReorderSJMapLayersOnMapService);
  private titilerNewService: TitilerNewService = inject(TitilerNewService);
  private analyticsToolService: AnalyticsToolService =
    inject(AnalyticsToolService);
  private analyticsDrawState: AnalyticsDrawStateService = inject(
    AnalyticsDrawStateService,
  );
  private rasterResolver: RasterTargetResolverService = inject(
    RasterTargetResolverService,
  );
  private kpiRasterService: KpiRasterService = inject(KpiRasterService);
  private simulationDrawClassesService = inject(SimulationDrawClassesService);
  private martinService: MartinService = inject(MartinService);
  // To do: Insert map loaded decorator
  // usiamo globalThis per fare riferimento a map di TS e non di maplibre
  layerOpacityMap = new globalThis.Map<string, number>();
  // Avoid DI cycle: access splitmap service via static instance

  DEFAULT_MAP_BASEMAP =
    'https://services.arcgisonline.com/arcgis/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
  // `https://api.mapbox.com/styles/v1/${environment.maps.userGis}/${environment.maps.mapBackgroundCode['satellite_street']}/tiles/256/{z}/{x}/{y}?access_token=${environment.maps.gisToken}`
  DEFAULT_MAP_SETTINGS: ISJMapSettings = {
    center: [0, 0],
    zoom: 2,
    style: {
      version: 8 as const,
      glyphs: '/assets/fonts/{fontstack}/{range}.pbf',
      sources: {
        'mapbox-xyz': {
          type: 'raster',
          tiles: [this.DEFAULT_MAP_BASEMAP],
          tileSize: 256,
        },
      },
      layers: [
        {
          id: 'mapbox-xyz',
          type: 'raster',
          source: 'mapbox-xyz',
          minzoom: 0,
          maxzoom: 22,
        },
      ],
    },
  };
  DEFAULT_DRAW_STYLE = createMapLibreDrawStyles();
  private map: MapLibreMap | null = null;
  readonly mainMap$: BehaviorSubject<MapLibreMap | null> =
    new BehaviorSubject<MapLibreMap | null>(null);
  private draw!: MapboxDraw;
  private splitToMainMirror = new Map<string, string>();

  isDrawing: BehaviorSubject<{
    status: boolean;
    layerId: string | null;
    analytics: boolean;
  }> = new BehaviorSubject({
    status: false,
    layerId: null,
    analytics: false,
  } as {
    status: boolean;
    layerId: string | null;
    analytics: boolean;
  });

  private drawCountMap = new globalThis.Map<string, number>([
    ['Point', 0],
    ['Polygon', 0],
    ['LineString', 0],
  ]);
  getSJMap(jsonUrl: string): Observable<ISJMap> {
    return this.http.get<ISJMap>(jsonUrl);
  }

  loadMap(map: MapLibreMap) {
    if (map) {
      this.map = map;
      this.map.setMaxZoom(18);
      this.mainMap$.next(this.map);
      this.map.on('click', (e) => {
        this.map.dragPan.enable();
      });

      this.addPmTilesProcotol();
      this.initializeDraw();
    }
    // aggiunto solo per ESRi
    this.map.addControl(new maplibregl.AttributionControl({ compact: true }));
  }

  addPmTilesProcotol() {
    // REGISTRAZIONE PROTOCOLLO
    const protocol = new Protocol();
    addProtocol('pmtiles', protocol.tile);
  }

  getMap(): MapLibreMap | null {
    return this.map;
  }

  initializeDraw() {
    if (!this.map) return;

    this.draw = new MapboxDraw({
      displayControlsDefault: false,
      styles: this.DEFAULT_DRAW_STYLE,
      userProperties: true,
    });

    this.map.addControl(this.draw, 'top-right');

    if (this.simulationDrawClassesService) {
      this.simulationDrawClassesService.setSplitDraw(this.draw);
    }

    // Aggiungi gli eventi di disegno dopo aver inizializzato il controllo
    this.map.on('draw.create', this.onDrawCreate.bind(this));
    this.map.on('draw.update', this.onDrawUpdate.bind(this));

    this.map.on('mousemove', (e) => {
      const mode = (this.draw as any).getMode();
      if (this.isDrawing.value.analytics && mode === 'draw_polygon') {
        const data = this.draw.getAll();
        const currentFeature = data.features[data.features.length - 1];
        if (
          currentFeature &&
          currentFeature.geometry &&
          currentFeature.geometry.type === 'Polygon'
        ) {
          const calculatedArea = area(currentFeature);
          let areaText = '';
          if (calculatedArea < 1000000) {
            areaText = `${Math.round(calculatedArea).toLocaleString()} m²`;
          } else {
            areaText = `${(calculatedArea / 1000000).toFixed(2)} km²`;
          }

          this.drawingTooltip
            .setLngLat(e.lngLat)
            .setHTML(
              `<div style="padding: 5px; font-weight: bold; color: #333;">Area: ${areaText}</div>`,
            )
            .addTo(this.map!);
        }
      } else {
        if (this.drawingTooltip.isOpen()) {
          this.drawingTooltip.remove();
        }
      }
    });

    // Bind click handler for drawn features
    this.bindDrawFeatureClickPopup();
  }

  // To refine: default settings
  addDefaultSettings() {
    if (this.map) {
      this.map.setStyle(this.DEFAULT_MAP_SETTINGS.style);
      this.map.setCenter(this.DEFAULT_MAP_SETTINGS.center);
      this.map.setZoom(this.DEFAULT_MAP_SETTINGS.zoom);
    }
  }

  addSource(
    name: string,
    source: SourceSpecification | CanvasSourceSpecification,
  ) {
    this.map.addSource(name, source);
  }

  addLayer(layer: AddLayerObject) {
    if (!this.map) return;
    const findSameDrawLayer: boolean = this.map
      .getStyle()
      .layers.find((l) => l.id === layer.id && layer.id.startsWith('gl-draw'))
      ?.id
      ? true
      : false;
    if (findSameDrawLayer) {
      return;
    }
    if (layer.id.startsWith('gl-draw') || this.isBaseLayer(layer.id)) {
      this.map.addLayer(layer);
    } else {
      const findedDrawLayer: string =
        this.map.getStyle().layers.find((l) => l.id.startsWith('gl-draw'))
          ?.id || '';

      if (findedDrawLayer) {
        this.map.addLayer(layer, findedDrawLayer);
      } else {
        this.map.addLayer(layer);
      }
    }
  }

  enableDraw(
    mode: 'point' | 'line' | 'line_string' | 'polygon',
    layerId: string = null,
    analytics: boolean = true,
  ): void {
    this.isDrawing.next({ status: true, layerId, analytics });
    this.draw.changeMode(`draw_${mode}`);
  }

  disableDraw(): void {
    this.draw.changeMode('simple_select');
    this.isDrawing.next({ status: false, layerId: null, analytics: false });
  }

  private async maplibreRasterLayerIdToSJMapUUID(
    layerId: string,
  ): Promise<string | null> {
    return new Promise((resolve) => {
      this.store
        .select(selectExplorerSJMap)
        .pipe(take(1))
        .subscribe((sjmapState: ExplorerSJMapState) => {
          const allLayers = sjmapState?.sjmap?.layers || ([] as any[]);
          for (const layer of allLayers) {
            if (layer?.type !== 'raster') continue;
            const sources = (layer as any).sources || [];
            for (const src of sources) {
              const ml = src?.maplibre;
              if (ml && typeof ml.layer === 'string' && ml.layer === layerId) {
                resolve(layer.uuid);
                return;
              }
            }
          }
          resolve(null);
        });
    });
  }

  async resolveTopVisibleRasterUUID(): Promise<string | null> {
    return this.rasterResolver.resolveTargetRasterUUID({
      map: this.map,
      isBaseLayer: (id) => this.isBaseLayer(id),
      maplibreIdToUUID: this.maplibreRasterLayerIdToSJMapUUID.bind(this),
    });
  }

  async onDrawCreate(e: { features: Feature[] }): Promise<void> {
    const feature = e.features[0];
    const featureId: string = feature.id as string;

    if (!feature.properties?.['label']) {
      const geometryType = feature.geometry.type;
      const label = this.generateReadableName(geometryType);
      this.draw.setFeatureProperty(featureId, 'label', label);
    }

    if (this.isDrawing.value.analytics) {
      const targetLayerUUID =
        (await this.rasterResolver.resolveTargetRasterUUID({
          map: this.map,
          geometry: feature.geometry,
          isBaseLayer: (id) => this.isBaseLayer(id),
          maplibreIdToUUID: this.maplibreRasterLayerIdToSJMapUUID.bind(this),
        })) || this.isDrawing.value.layerId;

      if (targetLayerUUID) {
        this.analyticsToolService.changeActiveLayerUUIDTab(targetLayerUUID);
        this.store.dispatch(
          addAnalyticsData({ layerUUID: targetLayerUUID, name: featureId }),
        );
      }
    }

    // Se la modalità Point Draw è ancora attiva, riattiva il draw dopo ogni punto
    if (this.analyticsDrawState.isMainPointActive()) {
      setTimeout(async () => {
        const dynamicUUID = await this.resolveTopVisibleRasterUUID();
        const targetLayerUUID = dynamicUUID || this.isDrawing.value.layerId;
        if (targetLayerUUID) this.enableDraw('point', targetLayerUUID);
        else
          this.isDrawing.next({
            status: false,
            layerId: null,
            analytics: false,
          });
      }, 0);
    } else if (this.analyticsDrawState.isMainPolygonActive()) {
      setTimeout(async () => {
        const dynamicUUID = await this.resolveTopVisibleRasterUUID();
        const targetLayerUUID = dynamicUUID || this.isDrawing.value.layerId;
        if (targetLayerUUID) this.enableDraw('polygon', targetLayerUUID);
        else
          this.isDrawing.next({
            status: false,
            layerId: null,
            analytics: false,
          });
      }, 0);
    } else {
      this.isDrawing.next({
        status: false,
        layerId: null,
        analytics: false,
      });
    }
  }

  async onDrawUpdate(e: { features: Feature[] }): Promise<void> {
    if (!e.features?.length) return;
    for (const feature of e.features) {
      if (
        !feature.properties?.['simulationArea'] &&
        !feature.properties?.['intervention']
      ) {
        await this.recalcAnalyticsForFeature(feature.id as string);
      }
    }
  }

  setDrawFeatureProperty(
    drawFeatureId: string,
    property: string,
    value: unknown,
  ): void {
    if (this.draw) {
      const feature = this.draw.get(drawFeatureId);
      if (feature) {
        this.draw.setFeatureProperty(drawFeatureId, property, value);
        return;
      }
    }
    // Fallback: prova anche sulla splitmap
    let splitMapFeatureIds: string[] = [];
    try {
      const splitMapService =
        (window as any).ng?.getInjector?.(
          require('frontend/src/app/services/explorer/explorer-splitmap/explorer-splitmap.service')
            .ExplorerSplitMapService,
        ) || null;
      if (splitMapService && splitMapService.draw) {
        splitMapFeatureIds = splitMapService.draw
          .getAll()
          .features.map((f: Feature) => f.id);
        const splitFeature = splitMapService.draw.get(drawFeatureId);
        if (splitFeature) {
          splitMapService.draw.setFeatureProperty(
            drawFeatureId,
            property,
            value,
          );
          return;
        }
      }
    } catch (e) {
      // fallback non disponibile
    }
    const mainMapFeatureIds = this.draw
      ? this.draw.getAll().features.map((f: Feature) => f.id)
      : [];
    // console.warn(`[setDrawFeatureProperty] Feature ${drawFeatureId} non trovata né su main map né su splitmap.\nMainMap features:`, mainMapFeatureIds, '\nSplitMap features:', splitMapFeatureIds);
  }

  enabledRasterDrawingsPopupSet = new Set<string>(); // Set per evitare di aggiungere più volte lo stesso listener
  popup = new Popup({
    closeButton: true,
    closeOnClick: true,
    maxWidth: '450px',
  });
  drawingTooltip: Popup = new Popup({
    closeButton: false,
    closeOnClick: false,
    className: 'drawing-area-tooltip',
    maxWidth: '300px',
    offset: 15,
  });

  public mainMapFeaturePopups: globalThis.Map<string, maplibregl.Popup> =
    new globalThis.Map();

  private readonly rasterDrawingClickHandler = (
    e: MapLayerMouseEvent,
  ): void => {
    const clickedFeatureId =
      this.map.queryRenderedFeatures(e.point)?.[0]?.properties['id'] || null;
    if (
      !clickedFeatureId ||
      !this.enabledRasterDrawingsPopupSet.has(clickedFeatureId)
    ) {
      return;
    }

    const rasterDrawFeature: Feature | undefined =
      this.getDrawFeatureFromId(clickedFeatureId);
    if (!rasterDrawFeature || !rasterDrawFeature.properties['analyticsData']) {
      return;
    }

    const tableColumn: RasterDrawingTableColumn[] = [
      { key: 'name', label: 'Date' },
      { key: 'value', label: 'Value' },
    ];

    const rasterDrawValues: {
      'reference-date': string;
      value: number;
    }[] = rasterDrawFeature.properties['analyticsData'];

    const tableRows: RasterDrawingTableData[] = rasterDrawValues.map(
      (rasterDrawValue) => ({
        name: rasterDrawValue['reference-date'].split('T')[0],
        value: this.roundToDecimalsAlt(rasterDrawValue.value, 4),
      }),
    );

    this.popup
      .setLngLat(e.lngLat)
      .setDOMContent(this.formatTable(tableColumn, tableRows))
      .addTo(this.map);
  };

  enableRasterDrawingsPopups(drawFeatureId: string): void {
    if (!this.map) {
      console.error('Map not initialized');
      return;
    }

    this.enabledRasterDrawingsPopupSet.add(drawFeatureId);

    const drawLayers = this.map
      .getStyle()
      .layers.map((layer) => layer.id)
      .filter((layerId) => layerId.startsWith('gl-draw'));
    drawLayers.forEach((layerId) => {
      this.map.off('click', layerId, this.rasterDrawingClickHandler);
      this.map.on('click', layerId, this.rasterDrawingClickHandler);
    });
  }

  roundToDecimalsAlt(num: number, decimals: number): number {
    const factor = Math.pow(10, decimals);
    return Math.round(num * factor) / factor;
  }

  formatTable(
    columns: { key: string; label: string }[],
    data: { [key: string]: number | string }[],
  ): HTMLElement {
    const container = document.createElement('div');
    container.classList.add('popup-content');

    if (data.length === 0) {
      container.textContent = 'No data available';
      return container;
    }

    const table = document.createElement('table');
    table.classList.add('table', 'table-striped', 'table-bordered');

    const thead = document.createElement('thead');
    thead.classList.add('thead-dark');
    const headerRow = document.createElement('tr');
    columns.forEach((col) => {
      const th = document.createElement('th');
      th.textContent = col.label;
      headerRow.appendChild(th);
    });
    thead.appendChild(headerRow);
    table.appendChild(thead);

    const tbody = document.createElement('tbody');
    data.forEach((row) => {
      const tr = document.createElement('tr');
      columns.forEach((col) => {
        const td = document.createElement('td');
        td.textContent = String(row[col.key] ?? '');
        tr.appendChild(td);
      });
      tbody.appendChild(tr);
    });
    table.appendChild(tbody);

    container.appendChild(table);
    return container;
  }

  getMapSources(): {
    [_: string]: SourceSpecification;
  } {
    return this.map.getStyle().sources;
  }

  clearMap(): void {
    if (this.map) {
      // Rimuovi tutti i layer
      const layers = this.map.getStyle().layers;
      if (layers) {
        for (const layer of layers) {
          if (this.map.getLayer(layer.id)) {
            this.map.removeLayer(layer.id);
          }
        }
      }

      // Rimuovi tutte le sorgenti
      const sources = Object.keys(this.map.getStyle().sources);
      for (const sourceId of sources) {
        if (this.map.getSource(sourceId)) {
          this.map.removeSource(sourceId);
        }
      }
    }
  }

  losdSJMap2(): void {
    if (this.map) {
      this.store
        .select(selectExplorerSJMap)
        .pipe(take(1)) // Seleziono una sola volta (altrimenti il subscribe emette la modifica ogni volta)
        .subscribe((explorerSJMap: ExplorerSJMapState) => {
          // Carica le impostazioni della mappa
          this.addDefaultSettings();

          // SJMap 2.0
          this.addSJMapLayersToMap(explorerSJMap.sjmap.layers);

          // TEST ZONE
          // this.store.select(selectExplorerSJMap).pipe(
          //   take(1)
          // ).subscribe((sjmap: ExplorerSJMapState) => {
          //   console.log("SJMap Layers: ", sjmap.sjmap);
          //   console.log("MapLibre Layers: ", this.map.getStyle().layers);
          // })

          // Zoomma alla bounding box del primo layer della SJMap
          this.zoomToFirstLayer(explorerSJMap);
        });
    }
  }

  private zoomToFirstLayer(explorerSJMapState: ExplorerSJMapState) {
    const comingFromGeoSimulator = !!localStorage.getItem(
      'preferredMasterplanUUID',
    );
    if (comingFromGeoSimulator) {
      return;
    }
    const layers = explorerSJMapState?.sjmap?.layers;
    if (!layers || !layers.length) {
      return;
    }

    const SJMLayer = layers[0];
    if (!SJMLayer) {
      return;
    }

    if (SJMLayer.type === 'raster') {
      this.store
        .select(selectExplorerSJMapRasterActiveSourceByUUID(SJMLayer.uuid))
        .pipe(
          filter((activeSource) => !!activeSource),
          take(1),
        )
        .subscribe((activeSource) => {
          if (!activeSource) return;
          this.titilerNewService.zoomToRaster(activeSource.source).subscribe({
            next: (zoomInfo: IZoom) => {
              if (zoomInfo?.bounds?.length === 4) {
                this.zoomToBoundingBox(zoomInfo.bounds);
              }
            },
          });
        });
    } else {
      this.store
        .select(selectExplorerSJMapVectorActiveSourceByUUID(SJMLayer.uuid))
        .pipe(
          filter((activeSource) => !!activeSource),
          take(1),
        )
        .subscribe(() => {
          const onClickZoom = async () => {
            const bboxList: [number, number, number, number][] = [];

            for (const source of SJMLayer.sources) {
              try {
                const bbox = await this.getVectorSourceBbox(source);
                bboxList.push(bbox);
              } catch (err) {
                console.error(
                  'Errore nel recupero bbox per:',
                  source.source,
                  err,
                );
              }
            }

            if (bboxList.length > 0) {
              const finalBbox = bboxList.reduce(
                (acc, curr) => {
                  const [minX, minY, maxX, maxY] = curr;
                  return [
                    Math.min(acc[0], minX),
                    Math.min(acc[1], minY),
                    Math.max(acc[2], maxX),
                    Math.max(acc[3], maxY),
                  ] as [number, number, number, number];
                },
                [Infinity, Infinity, -Infinity, -Infinity] as [
                  number,
                  number,
                  number,
                  number,
                ],
              );
              const [minLon, minLat, maxLon, maxLat] = finalBbox;
              if (this.map) {
                this.map.fitBounds(
                  [
                    [minLon, minLat],
                    [maxLon, maxLat],
                  ],
                  { padding: 20 },
                );
              }
            }
          };

          onClickZoom();
        });
    }
  }

  /**
   * Aggiunge degli SJMapLayers in cima alla mappa in ordine, rispettando la gerarchia dei layer
   * @param SJMapLayers Array di layer della SJMap da aggiungere alla mappa
   */
  addSJMapLayersToMap(SJMapLayers: TLayer[]) {
    // Aggiungi in ordine i layer della SJMap alla mappa

    SJMapLayers.forEach((currSJMLayer: TLayer, index: number) => {
      const prevSJMLayer = SJMapLayers[index - 1] ?? null; // Prendi il layer precedente (nella SJMap) o null se non c'è
      if (prevSJMLayer) {
        this.addSJMapLayerToMap(currSJMLayer, prevSJMLayer); // Aggiungi il layer sotto il layer precedente
      } else {
        this.addSJMapLayerToMap(currSJMLayer); // Aggiungi il layer sopra a "TUTTI GLI ALTRI" (ci saranno ulteriori check a valle)
      }
    });
  }

  /**
   * Aggiunge un SJMapLayer alla mappa
   * @param SJMapLayer Layer da aggiungere alla mappa
   * @param prevSJMLayer Layer precedente (nella SJMap) da usare come riferimento per l'ordine. Se non c'è, il layer viene aggiunto sopra a tutti gli altri
   */
  addSJMapLayerToMap(SJMapLayer: TLayer, prevSJMLayer?: TLayer) {
    this.explorerAddSJMapLayerToMapService.addSJMapLayerToMap(
      this.map,
      SJMapLayer,
      prevSJMLayer,
    );
  }

  reorderLayersOnMap(SJMapLayers: TLayer[]) {
    this.explorerReorderSJMapLayersOnMapService.reorderSJMapLayersOnMap(
      this.map,
      SJMapLayers,
    );
  }

  addLayerInSJMap(layer: TLayer) {
    this.store.dispatch(addLayer({ layer }));
  }

  zoomToGeometry(geometry: Geometry | Feature): void {
    if (!this.map) {
      console.error('Mappa non inizializzata');
      return;
    }

    // Calcola il bounding box utilizzando Turf.js
    const bounds = bbox(geometry); // Restituisce [minLon, minLat, maxLon, maxLat]

    // Passa il bounding box direttamente a fitBounds
    this.map.fitBounds(
      [
        [bounds[0], bounds[1]],
        [bounds[2], bounds[3]],
      ], // Converti in formato bidimensionale
      {
        padding: 20, // Aggiungi padding
        maxZoom: 15, // Limita lo zoom massimo
      },
    );
  }

  /**
   * Sets the opacity for a specified layer on the map.
   * Dynamically determines the appropriate paint property to update
   * based on the type of the layer (e.g., fill, line, symbol, raster).
   *
   * @param layerId - The ID of the layer to update.
   * @param opacity - The opacity value to set (range: 0 to 1).
   */
  setLayerOpacity(
    layerId: string,
    opacity: number,
    targetMap?: MapLibreMap,
  ): void {
    const map = targetMap || this.map;
    if (!map) return;

    const layer = map.getLayer(layerId);
    if (!layer) {
      // console.warn(`Layer with ID ${layerId} not found on selected map.`);
      return;
    }

    this.layerOpacityMap.set(layerId, opacity);

    switch (layer.type) {
      case 'fill':
        map.setPaintProperty(layerId, 'fill-opacity', opacity);
        break;
      case 'line':
        map.setPaintProperty(layerId, 'line-opacity', opacity);
        break;
      case 'symbol':
        map.setPaintProperty(layerId, 'icon-opacity', opacity);
        map.setPaintProperty(layerId, 'text-opacity', opacity);
        break;
      case 'raster':
        map.setPaintProperty(layerId, 'raster-opacity', opacity);
        break;
      default:
      // console.warn(`Unsupported layer type: ${layer.type}`);
    }
  }

  /**
   * Returns the current opacity value for the specified layer.
   * If no opacity is set for the layer, it returns the default value of 1.
   *
   * @param uuid - The unique identifier of the layer.
   * @returns The opacity value (between 0 and 1) for the layer.
   */
  getLayerOpacity(uuid: string): number {
    return this.layerOpacityMap.get(uuid) ?? 0.8;
  }

  /**
   * Toggles the visibility of all layers associated with a specific source ID.
   *
   * @param sourceId - The source ID to match layers.
   * @param isVisible - Whether the layers should be visible (true) or hidden (false).
   * @param map - The map instance to operate on. Defaults to the current map instance.
   */
  toggleLayerVisibilityBySource(
    sourceId: string,
    isVisible: boolean,
    map: MapLibreMap = this.map,
  ): void {
    // Retrieve all layers from the map's style
    const layers: LayerSpecification[] = map.getStyle()?.layers || [];

    // Filter layers to find those matching the sourceId
    const matchingLayers = layers.filter(
      (layer): layer is LayerSpecification & { source: string } =>
        'source' in layer && layer.source === sourceId,
    );

    // Set the `visibility` property for each matching layer
    const visibility = isVisible ? 'visible' : 'none';
    matchingLayers.forEach((layer) => {
      map.setLayoutProperty(layer.id, 'visibility', visibility);
      const labelLayerId = `${layer.id}-label`;
      if (map.getLayer(labelLayerId)) {
        map.setLayoutProperty(labelLayerId, 'visibility', visibility);
      }
    });
  }
  zoomToBoundingBox(bbox: number[], map: MapLibreMap = this.map): void {
    if (!map) return;

    const [minLon, minLat, maxLon, maxLat] = bbox;

    map.fitBounds(
      [
        [minLon, minLat],
        [maxLon, maxLat],
      ],
      {
        padding: 20,
        maxZoom: 14,
      },
    );
  }

  private isBaseLayer(layerId: string): boolean {
    const baseLayerIds = ['mapbox-xyz', 'basemap-layer']; // Aggiungi gli ID reali della tua mappa
    return baseLayerIds.includes(layerId);
  }

  getRasterLayers(): LayerSpecification[] {
    if (!this.map) {
      console.error('Mappa non inizializzata');
      return [];
    }

    return this.map
      .getStyle()
      ?.layers.filter(
        (layer) => layer?.type === 'raster' && !this.isBaseLayer(layer?.id),
      );
  }

  getDrawFeatureFromId(featureId: string): Feature | undefined {
    return this.draw.get(featureId);
  }

  getSource(sourceId: string): Source {
    return this.map.getSource(sourceId);
  }

  getDrawnFeature(id: string): Feature | null {
    if (!this.draw) return null;

    return this.draw.get(id);
  }

  generateReadableName(geometryType: string): string {
    const currentCount = this.drawCountMap.get(geometryType) ?? 0;
    const newCount = currentCount + 1;
    this.drawCountMap.set(geometryType, newCount);
    return `${geometryType} ${newCount}`;
  }

  bindDrawFeatureClickPopup() {
    if (!this.map) return;
    this.map.on('click', (e) => {
      const currentMode =
        (this.draw as any).getMode?.() || (this.draw as any).getMode;
      if (currentMode && currentMode !== 'simple_select') {
        return;
      }
      const features = this.map.queryRenderedFeatures(e.point, {
        layers: this.map
          .getStyle()
          .layers.filter(
            (l) =>
              l.id.startsWith('gl-draw') &&
              !l.id.includes('midpoint') &&
              !l.id.includes('vertex'),
          )
          .map((l) => l.id),
      });
      if (!features.length) return;
      const feature = features[0];
      const featureId = feature.properties && feature.properties['id'];
      if (!featureId) return;
      const logicalFeature = this.draw.get(featureId);
      if (!logicalFeature) return;

      this.mainMapFeaturePopups.set(featureId, this.popup);

      // 👉 Salta popup cestino per feature con "intervention" (simulazioni)
      if (
        (logicalFeature.properties &&
          'intervention' in logicalFeature.properties) ||
        (logicalFeature.properties &&
          'simulationArea' in logicalFeature.properties)
      ) {
        // Ora il popup simulation viene gestito dal SimulationDrawClassesService, quindi qui non mostro nulla

        // if (logicalFeature.properties &&
        //   'simulationArea' in logicalFeature.properties) {
        //     this.simulationDrawClassesService.disableDrawSimulation();
        //   }

        return;
      }

      const mirroredFromSplitId = logicalFeature.properties?.['mirrorOf'];
      const canMirror =
        !mirroredFromSplitId && this.hasVisibleRasterOnSplitMap();
      const isMirrored = this.hasMirroredFeatureForSplit(featureId);

      const currentName =
        (logicalFeature.properties && logicalFeature.properties['label']) ||
        this.generateReadableName(logicalFeature.geometry?.type || 'Point');

      const mirrorCheckboxHTML = canMirror
        ? `
          <div class="form-check mb-2" style="color:#FFFFFF">
            <input class="form-check-input mirror-feature-checkbox" type="checkbox" id="mirror-feature-checkbox" ${
              isMirrored ? 'checked' : ''
            }>
            <label class="form-check-label" style="color:black" for="mirror-feature-checkbox">Mirror to Split Map</label>
          </div>
        `
        : '';

      let areaHtml = '';
      if (logicalFeature.geometry) {
        const calculatedArea = area(logicalFeature);
        let areaText = '';
        if (calculatedArea < 1000000) {
          areaText = `${Math.round(calculatedArea).toLocaleString()} m²`;
        } else {
          areaText = `${(calculatedArea / 1000000).toFixed(2)} km²`;
        }
        areaHtml = `<div class="mb-2" style="color: black; font-weight: 500;">Area: ${areaText}</div>`;
      }

      let popupContent = `
          <div class="popup-actions mt-3">
             ${areaHtml}
            <div class="mb-2">
              <label for="feature-name-input" class="form-label" style="color:black; font-weight:600;">Name</label>
              <input class="form-control form-control-sm" type="text" id="feature-name-input" >
              <button class="btn btn-primary btn-sm w-100 mt-2" id="save-feature-name-btn">Save name</button>
            </div>
            ${mirrorCheckboxHTML}
            <button class="btn btn-danger btn-sm w-100" id="delete-feature-btn"><svg width="20px" height="20px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
              <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
              <g id="SVGRepo_iconCarrier">
                <path d="M3 6H5H21" stroke="#FFFFFF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M8 6V4C8 3.44772 8.44772 3 9 3H15C15.5523 3 16 3.44772 16 4V6M19.21 6L18.2761 19.1422C18.2268 19.8422 17.6397 20.3924 16.9378 20.3924H7.0622C6.36034 20.3924 5.77322 19.8422 5.72386 19.1422L4.79 6H19.21Z" stroke="#FFFFFF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </g>
              </svg>
              </button>
          </div>
        `;

      this.popup.setLngLat(e.lngLat).setHTML(popupContent).addTo(this.map);

      setTimeout(() => {
        const popupElement = document.querySelector(
          '.maplibregl-popup-content',
        );
        if (popupElement) {
          popupElement.classList.add('popup-actions');
        }

        if (canMirror) {
          const popupEl = this.popup.getElement();
          const checkbox = popupEl.querySelector(
            '.mirror-feature-checkbox',
          ) as HTMLInputElement | null;
          if (checkbox) {
            checkbox.dataset['featureId'] = String(featureId);
            checkbox.addEventListener('change', () => {
              if (!this.map) return;
              if (checkbox.checked) {
                const mirroredId =
                  this.addMirroredFeatureToSplit(logicalFeature);
                if (!mirroredId) {
                  checkbox.checked = false;
                }
              } else {
                this.removeMirroredFeatureForSplit(featureId);
              }
            });
          }
        }

        const popupEl = this.popup.getElement();
        const saveBtn = popupEl.querySelector<HTMLElement>(
          '#save-feature-name-btn',
        );
        const nameInput = popupEl.querySelector(
          '#feature-name-input',
        ) as HTMLInputElement | null;
        if (saveBtn && nameInput) {
          nameInput.value = currentName;
          saveBtn.onclick = () => {
            const newName = nameInput.value?.trim() || currentName;
            try {
              this.setDrawFeatureProperty(featureId, 'label', newName);
            } catch {}
            try {
              const {
                updateAnalyticsPointName,
              } = require('../store/actions/explorer/explorer-analytics-tool.actions');
              this.store.dispatch(
                updateAnalyticsPointName({ featureId, newName }),
              );
            } catch {}
          };
        }

        const btn = popupEl.querySelector<HTMLElement>('#delete-feature-btn');
        if (btn) {
          btn.onclick = () => {
            if (mirroredFromSplitId) {
              ExplorerSplitMapService.instance?.removeMirroredFeatureForMain(
                mirroredFromSplitId,
              );
              this.popup.remove();
              return;
            }

            this.draw.delete(featureId);
            this.popup.remove();
            this.store.dispatch(cleanAnalyticsData({ featureId }));
            this.removeMirroredFeatureForSplit(featureId);
          };
        }
      }, 0);
    });
  }

  hasMirroredFeatureForSplit(originalId: string): boolean {
    const splitSvc = ExplorerSplitMapService.instance;
    const splitDraw = (splitSvc as any)?.draw as any;
    if (!splitDraw) return false;
    const mirroredId = this.splitToMainMirror.get(originalId);
    if (mirroredId) {
      try {
        const f = splitDraw.get(mirroredId);
        if (f) return true;
        // stale mapping -> drop it
        this.splitToMainMirror.delete(originalId);
      } catch {}
    }
    // fallback: search by mirrorOf property
    try {
      const all = splitDraw.getAll?.().features || [];
      const found = all.find(
        (f: Feature) => f?.properties?.['mirrorOf'] === originalId,
      );
      if (found && found.id) {
        this.splitToMainMirror.set(originalId, found.id);
        return true;
      }
    } catch {}
    return false;
  }

  addMirroredFeatureToSplit(originalFeature: Feature): string | null {
    try {
      const splitSvc = ExplorerSplitMapService.instance;
      const splitMap = splitSvc?.splitMap$.value;
      const splitDraw = (splitSvc as any)?.draw as any;
      if (!splitMap || !splitDraw) return null;
      const clone: Feature = {
        type: 'Feature',
        properties: {
          ...(originalFeature.properties || {}),
          mirrorOf: originalFeature.id,
        },
        geometry: JSON.parse(JSON.stringify(originalFeature.geometry)),
      };
      const res = splitDraw.add(clone);
      const newId = Array.isArray(res) ? res[0] : res;
      if (typeof newId === 'string') {
        this.splitToMainMirror.set(originalFeature.id as string, newId);
        // Compute target raster UUID on split map and dispatch analytics for mirrored point
        (async () => {
          const targetLayerUUID =
            (await this.rasterResolver.resolveTargetRasterUUID({
              map: splitMap,
              geometry: clone.geometry,
              allowedUUIDs: splitSvc.splitMapLayers$.value,
              isBaseLayer: (id) => this.isBaseLayer(id),
              maplibreIdToUUID:
                this.maplibreRasterLayerIdToSJMapUUID.bind(this),
            })) || splitSvc.splitMapLayers$.value.at(-1);

          if (targetLayerUUID) {
            this.analyticsToolService.changeActiveLayerUUIDTab(targetLayerUUID);
            this.store.dispatch(
              addAnalyticsData({ layerUUID: targetLayerUUID, name: newId }),
            );
          }
        })().catch(() => {});
        return newId;
      }
      return null;
    } catch {
      return null;
    }
  }

  removeMirroredFeatureForSplit(originalId: string): void {
    try {
      const splitSvc = ExplorerSplitMapService.instance;
      const splitDraw = (splitSvc as any)?.draw as any;
      const mirroredId = this.splitToMainMirror.get(originalId);
      if (splitDraw && mirroredId) {
        try {
          splitDraw.delete(mirroredId);

          const popup = splitSvc.splitFeaturePopups.get(mirroredId);
          if (popup) {
            popup.remove();
            splitSvc.splitFeaturePopups.delete(mirroredId);
          }

          // Dispatch clean analytics for mirrored feature on split map
          this.store.dispatch(cleanAnalyticsData({ featureId: mirroredId }));
        } catch {}
        this.splitToMainMirror.delete(originalId);
      }
      this.setMainMirrorCheckboxChecked(originalId, false);
    } catch {}
  }

  private setMainMirrorCheckboxChecked(
    originalId: string,
    checked: boolean,
  ): void {
    const popupEl = this.popup?.getElement();
    const checkbox = popupEl
      ? Array.from(
          popupEl.querySelectorAll<HTMLInputElement>(
            '.mirror-feature-checkbox',
          ),
        ).find((el) => el.dataset['featureId'] === String(originalId))
      : undefined;

    if (checkbox) {
      checkbox.checked = checked;
    }
  }

  closeAllAccordionsSubject = new Subject<void>();
  expandedAccordionLayerUUIDSubject = new Subject<string>();
  // openSimulationPopup(
  //   lngLat: [number, number],
  //   feature: GeoJSON.Feature,
  //   featureIndex: number
  // ) {
  //   if (!this.map) return;

  //   const container = document.createElement('div');
  //   container.classList.add('simulation-popup');
  //   const componentRef: ComponentRef<SimulationPopupComponent> = createComponent(SimulationPopupComponent, {
  //     environmentInjector: this.appRef.injector,
  //     elementInjector: this.injector,
  //   });

  //   componentRef.instance.featureProperties = feature;
  //   componentRef.instance.centroid = lngLat;
  //   componentRef.instance.geometryType = feature.geometry.type;
  //   componentRef.instance.featureIndex = featureIndex;
  //   componentRef.instance.areaInKm2 = null;

  //   this.appRef.attachView(componentRef.hostView);
  //   container.appendChild((componentRef.hostView as any).rootNodes[0]);

  //   this.popup.setDOMContent(container).setLngLat(lngLat).addTo(this.map);
  // }

  private livingLabMarkersMap = new Map<string, Array<Marker>>(); // Map nativa JS/TS
  private livingLabService: LivingLabService = inject(LivingLabService);
  private kpiSidebarService: KpiSidebarService = inject(KpiSidebarService);

  /**
   * Aggiunge o rimuove i marker Living Lab associati a un vector layer
   * @param vectorUuid uuid del layer vector
   * @param visible true per mostrare, false per nascondere
   * @param threads lista thread Living Lab
   * @param map istanza mappa
   */
  toggleLivingLabMarkers(
    vectorUuid: string,
    visible: boolean,
    threads: IThread[],
    map: MapLibreMap,
  ) {
    if (!map || !map.isStyleLoaded()) {
      map?.once('styledata', () =>
        this.toggleLivingLabMarkers(vectorUuid, visible, threads, map),
      );
      return;
    }

    if (this.livingLabMarkersMap.has(vectorUuid)) {
      this.livingLabMarkersMap
        .get(vectorUuid)
        .forEach((marker) => marker.remove());
      this.livingLabMarkersMap.delete(vectorUuid);
    }

    // 2. Se i marcatori devono essere nascosti (`visible: false`), abbiamo finito.
    if (!visible) {
      return;
    }

    // 3. Se devono essere visibili, creiamo un nuovo array per i marcatori.
    const newMarkers: Marker[] = [];

    threads
      .filter(
        (t) => t.vector_uuid === vectorUuid && t.geometry?.type === 'Point',
      )
      .forEach((thread) => {
        const coordinates = (thread.geometry as Point).coordinates as [
          number,
          number,
        ];

        // Crea un marcatore identico a quello di onMapClickForComment
        const marker = new Marker({ color: '#1E66BE' }) // Colore blu come richiesto
          .setLngLat(coordinates)
          .addTo(map);

        // Aggiungi l'evento click direttamente all'elemento HTML del marcatore
        const markerElement = marker.getElement();
        markerElement.style.cursor = 'pointer'; // Feedback visivo per l'utente

        markerElement.addEventListener('click', (e) => {
          // Impedisce che il click si propaghi alla mappa (evitando altri popup)
          e.stopPropagation();

          // Apri i dettagli del thread, come prima
          this.livingLabService.openThreadDetails(thread);
          this.kpiSidebarService.sidebarOpenSubject.next(false);
        });

        newMarkers.push(marker);
      });

    // 4. Salva il nuovo array di marcatori nella nostra mappa di gestione.
    if (newMarkers.length > 0) {
      this.livingLabMarkersMap.set(vectorUuid, newMarkers);
    }
  }

  private hasVisibleRasterOnSplitMap(): boolean {
    try {
      const splitSvc = ExplorerSplitMapService.instance;
      const splitMap = splitSvc?.splitMap$.value;

      if (!splitMap) {
        return false;
      }

      const layers = splitMap.getStyle().layers;
      if (!layers) {
        return false;
      }

      return layers.some(
        (layer) =>
          layer.type === 'raster' &&
          !this.isBaseLayer(layer.id) &&
          splitMap.getLayoutProperty(layer.id, 'visibility') !== 'none',
      );
    } catch (e) {
      console.warn('Could not check for raster layers on split map.', e);
      return false;
    }
  }

  async recalcAnalyticsForFeature(featureId: string) {
    const feature = this.getDrawFeatureFromId(featureId);
    const targetLayerUUID =
      (await this.rasterResolver.resolveTargetRasterUUID({
        map: this.map,
        geometry: feature?.geometry ?? null,
        isBaseLayer: (id) => this.isBaseLayer(id),
        maplibreIdToUUID: this.maplibreRasterLayerIdToSJMapUUID.bind(this),
      })) || this.isDrawing.value.layerId;

    if (targetLayerUUID) {
      this.analyticsToolService.changeActiveLayerUUIDTab(targetLayerUUID);
      this.store.dispatch(cleanAnalyticsData({ featureId }));
      this.store.dispatch(
        addAnalyticsData({ layerUUID: targetLayerUUID, name: featureId }),
      );
    }
  }

  private async getVectorSourceBbox(
    source: IVectorSource,
  ): Promise<[number, number, number, number]> {
    if (this.martinService.isMartinSource(source)) {
      return this.martinService.getBounds(source);
    }

    const pmtiles = new PMTiles(source.source);
    const header = await pmtiles.getHeader();
    return [header.minLon, header.minLat, header.maxLon, header.maxLat];
  }

  async zoomToPMTiles(pmtilesUrl: string): Promise<void> {
    if (!this.map) return;

    try {
      const pmtiles = new PMTiles(pmtilesUrl);
      const header = await pmtiles.getHeader();

      // header.minLon, minLat, maxLon, maxLat li hai già usati sopra
      const bbox: [number, number, number, number] = [
        header.minLon,
        header.minLat,
        header.maxLon,
        header.maxLat,
      ];

      this.zoomToBoundingBox(bbox, this.map);
    } catch (err) {
      console.error('zoomToPMTiles() failed for', pmtilesUrl, err);
    }
  }
  setKpiRasterStyle(
    colormap: TColormap | null,
    bounds?: { type: 'Polygon'; coordinates: number[][][] } | null,
  ): void {
    this.kpiRasterService.setKpiRasterStyle(colormap, bounds);
  }
  handleKpiRasterClick(
    payload: {
      scenario: unknown;
      key: string;
      uri: string;
      target?: 'main' | 'split';
      action?: 'TOGGLE' | 'MOVE_TARGET';
    },
    sourceKpiType: string | null,
  ): void {
    const mainMap = this.getMap();
    const splitSvc = ExplorerSplitMapService.instance;
    const splitMap = splitSvc?.splitMap$.value ?? null;
    const isSplitOpen = !!splitSvc?.isOpen$?.value;

    this.kpiRasterService.handleKpiRasterClick({
      payload,
      sourceKpiType,
      mainMap,
      splitMap,
      isSplitOpen,
    });
  }
  clearAllKpiRastersOnSplitClose(): void {
    const splitSvc = ExplorerSplitMapService.instance;
    const splitMap = splitSvc?.splitMap$.value ?? null;
    this.kpiRasterService.clearAllKpiRastersOnSplitClose(splitMap);
  }
}
